import { useRef } from 'react';
import { PhotoFrame } from '@/components/profile/PhotoFrame';
import { ButtonLink } from '@/components/ui/Button';
import { Reveal } from '@/components/ui/Reveal';
import { StickyCta } from '@/components/ui/StickyCta';
import { getCreatorContent, featuredSlug } from '@/content/registry';
import { useDocumentTitle } from '@/lib/hooks';
import styles from './HomePage.module.css';

/**
 * How many questions the landing page promises.
 *
 * Read from the featured profile's first round rather than from the batching
 * default: it is the same number the profile says, the same number the
 * progress bar counts towards, and the commitment somebody makes when they tap
 * the button. Hard-coding it, or deriving it from `ROUND_SIZE`, is how the
 * landing page came to advertise a number the first round did not actually ask.
 */
const FIRST_ROUND_SIZE =
  getCreatorContent(featuredSlug)?.rounds[0]?.questions.length ?? 0;

const STEPS = [
  {
    title: 'Read a real introduction',
    body: 'A page written by an actual person, not a bio squeezed into 300 characters between two gym photos.',
  },
  {
    title: `Answer ${FIRST_ROUND_SIZE} questions`,
    body: 'Some serious, some not. After each one you see how they answered, so you are getting to know them, not being marked.',
  },
  {
    title: 'See where you align',
    body: 'Three broad outcomes and a few honest notes. There are more questions if you want them, and stopping there is a whole answer on its own.',
  },
];

const TRUST = [
  {
    title: 'No account, ever',
    body: 'No sign-up, no password, no email verification. Open the link and start.',
  },
  {
    title: 'Answers stay on your phone',
    body: 'Everything is worked out in your browser. Nothing is sent unless you choose to send it.',
  },
  {
    title: 'You choose what to share',
    body: 'If you get in touch, you are shown the exact list of what goes with it before anything moves.',
  },
  {
    title: 'Nothing following you around',
    body: 'No advertising pixels, no third-party scripts, no profile built about you in the background.',
  },
];

function HeroArt() {
  return (
    <svg
      className={styles.heroArtSvg}
      viewBox="0 0 400 400"
      fill="none"
      role="img"
      aria-label="Two overlapping circles, with the shared area between them highlighted"
    >
      <defs>
        <clipPath id="awc-lens">
          <path d="M200 92a108 108 0 0 0 0 216 108 108 0 0 0 0-216Z" />
        </clipPath>
      </defs>

      {/* Faint concentric rings for depth, no visual noise. */}
      <circle cx="200" cy="200" r="186" stroke="var(--line-subtle)" strokeWidth="1" />
      <circle cx="200" cy="200" r="152" stroke="var(--line-subtle)" strokeWidth="1" />

      <circle
        cx="152"
        cy="200"
        r="108"
        fill="color-mix(in oklab, var(--voice-james) 14%, transparent)"
        stroke="var(--voice-james)"
        strokeOpacity="0.75"
        strokeWidth="1.5"
      />
      <circle
        cx="248"
        cy="200"
        r="108"
        fill="color-mix(in oklab, var(--accent) 12%, transparent)"
        stroke="var(--accent)"
        strokeWidth="1.5"
      />

      {/* The overlap, drawn with hatching so it reads as the shared part. */}
      <g clipPath="url(#awc-lens)">
        <rect
          x="80"
          y="80"
          width="240"
          height="240"
          fill="color-mix(in oklab, var(--accent) 20%, transparent)"
        />
      </g>

      <text
        x="200"
        y="205"
        textAnchor="middle"
        fill="var(--text-primary)"
        fontFamily="var(--font-display)"
        fontSize="19"
        fontWeight="600"
      >
        where we
      </text>
      <text
        x="200"
        y="228"
        textAnchor="middle"
        fill="var(--text-primary)"
        fontFamily="var(--font-display)"
        fontSize="19"
        fontWeight="600"
      >
        align
      </text>
    </svg>
  );
}

export function HomePage() {
  useDocumentTitle('Are We Compatible? — a dating profile that asks questions back');

  const featured = getCreatorContent(featuredSlug);

  /*
    The in-flow control the floating bar stands in for. It has to be a real one
    at the foot of the page rather than the hero button: the bar's rule is
    "up while the real invitation is below the fold, down once it is reached",
    and a target at the top of the page is never below the fold.
  */
  const closingRef = useRef<HTMLElement | null>(null);

  return (
    <div className={styles.page}>
      {/* --- Hero ---------------------------------------------------------- */}
      <section className={`${styles.section} ${styles.hero}`}>
        <div className={styles.heroInner}>
          <div>
            <p className={styles.eyebrow}>
              <span className={styles.eyebrowDot} aria-hidden="true" />
              Better questions, more honest introductions
            </p>

            <h1 className={styles.heroTitle}>
              A dating profile that <em>asks questions back</em>.
            </h1>

            <p className={styles.heroLede}>
              Most profiles talk at you. This one has a conversation with you. Ten
              questions, their answers alongside yours, and an honest look at where the two
              of you actually line up.
            </p>

            <div className={styles.heroActions}>
              {featured ? (
                <ButtonLink to={`/${featured.profile.slug}`} size="large" withArrow>
                  Meet {featured.profile.displayName}
                </ButtonLink>
              ) : null}
              <ButtonLink to="/privacy" variant="secondary" size="large">
                How this handles your data
              </ButtonLink>
            </div>

            <p className={styles.heroNote}>
              No account. No swiping. Nothing sent anywhere unless you decide to.
            </p>
          </div>

          <Reveal className={styles.heroArt} delay={120}>
            <HeroArt />
          </Reveal>
        </div>
      </section>

      {/*
        --- Featured profile ------------------------------------------------
        Directly under the hero, and with a face in it.

        The card lives here rather than fifth of six because of a reader's
        note: "there is a lot of explanation before I meet James." Most people
        arrive at /james from a card and never see this page at all, so the
        trim is light — but for anyone who does land here, the person should
        come before the argument for the product.
      */}
      {featured ? (
        <section className={`${styles.section} ${styles.featured}`}>
          <div className={styles.inner}>
            <Reveal className={styles.featuredCard}>
              <div className={styles.featuredPhoto}>
                <PhotoFrame photo={featured.profile.photos.primary} priority />
              </div>

              <div className={styles.featuredBody}>
                <p className={styles.featuredKicker}>Live now</p>
                <h2 className={styles.featuredTitle}>
                  {featured.profile.displayName}, {featured.profile.age}
                </h2>
                <p className={styles.featuredText}>{featured.profile.opening}</p>
                <ul className={styles.featuredMeta}>
                  <li className={styles.chip}>{featured.profile.location}</li>
                  <li className={styles.chip}>{featured.profile.occupation}</li>
                  <li className={styles.chip}>{FIRST_ROUND_SIZE} questions to start</li>
                </ul>
              </div>

              <div className={styles.featuredAction}>
                <ButtonLink to={`/${featured.profile.slug}`} size="large" withArrow block>
                  Read the introduction
                </ButtonLink>
              </div>
            </Reveal>
          </div>
        </section>
      ) : null}

      {/* --- What this is not ---------------------------------------------- */}
      <section className={`${styles.section} ${styles.band}`}>
        <div className={styles.bandInner}>
          <div>
            <h2 className={styles.bandTitle}>This is not a dating app.</h2>
          </div>

          <div className={styles.compare}>
            <div className={styles.compareCol}>
              <h3>What it is not</h3>
              <ul className={`${styles.compareList} ${styles.compareMuted}`}>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    —
                  </span>
                  A marketplace of strangers to sort through
                </li>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    —
                  </span>
                  A feed, a queue, or a stack of cards to swipe
                </li>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    —
                  </span>
                  A test with a pass mark
                </li>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    —
                  </span>
                  Somewhere you need an account to look around
                </li>
              </ul>
            </div>

            <div className={`${styles.compareCol} ${styles.compareYes}`}>
              <h3>What it is</h3>
              <ul className={styles.compareList}>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    ●
                  </span>
                  One person, one page, one honest introduction
                </li>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    ●
                  </span>
                  Shared with someone met in real life
                </li>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    ●
                  </span>
                  A three-minute way to find the important things out early
                </li>
                <li>
                  <span className={styles.compareGlyph} aria-hidden="true">
                    ●
                  </span>
                  An invitation to a conversation, nothing more
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- How it works --------------------------------------------------- */}
      <section className={`${styles.section} ${styles.steps}`}>
        <div className={styles.inner}>
          <div className={styles.sectionHead}>
            <h2>How it works</h2>
            <p>Three minutes, start to finish. You can stop at any point.</p>
          </div>

          <ol className={styles.stepList}>
            {STEPS.map((step, index) => (
              <Reveal as="li" key={step.title} delay={index * 90} className={styles.step}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {index + 1}
                </span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/*
        --- Trust -----------------------------------------------------------
        The heading used to be "Built to be safe to open", above a line about
        having been handed a card by someone you had just met. Both were meant
        kindly and both backfired: naming the risk out loud, in the first
        thirty seconds, reminded people to feel it. The four promises are the
        reassuring part and they stay, stated plainly and without the preamble.
      */}
      <section className={`${styles.section} ${styles.trust}`}>
        <div className={styles.inner}>
          <div className={styles.sectionHead}>
            <h2>How this works with your data</h2>
            <p>The short version, and it is the whole version.</p>
          </div>

          <div className={styles.trustGrid}>
            {TRUST.map((item, index) => (
              <Reveal key={item.title} delay={index * 70} className={styles.trustItem}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* --- Coming soon ---------------------------------------------------- */}
      <section className={`${styles.section} ${styles.soon}`}>
        <div className={styles.inner}>
          <div className={styles.soonCard}>
            <span className={styles.soonBadge}>Coming soon</span>
            <h2>Create your own</h2>
            <p>
              One day you will be able to build a page like this yourself, with your own
              questions and your own answers. It is not open yet. Right now there is
              exactly one profile here, and that is deliberate.
            </p>
          </div>
        </div>
      </section>

      {/* --- The one thing to do -------------------------------------------
          Last, and the only invitation on the page that is not competing with
          an explanation beside it. It is also what the floating bar is a copy
          of, which is why it exists as a real section rather than as a button
          that only ever appears at the bottom of the screen. */}
      {featured ? (
        <section className={`${styles.section} ${styles.closing}`} ref={closingRef}>
          <div className={styles.closingCard}>
            <h2 className={styles.closingTitle}>
              That is the whole idea. The rest is just {featured.profile.displayName}.
            </h2>
            <p className={styles.closingBody}>
              No account, no swiping, and nothing sent anywhere unless you decide to.
            </p>
            <ButtonLink to={`/${featured.profile.slug}`} size="large" withArrow>
              Meet {featured.profile.displayName}
            </ButtonLink>
          </div>
        </section>
      ) : null}

      {/* --- Floating invitation ---------------------------------------------
          Same pattern as the profile and result pages: the page's one real
          action, carried alongside the reading, standing down the moment the
          reader arrives at the real thing. */}
      {featured ? (
        <StickyCta standInFor={closingRef}>
          <ButtonLink to={`/${featured.profile.slug}`} size="large" block withArrow>
            Meet {featured.profile.displayName}
          </ButtonLink>
        </StickyCta>
      ) : null}
    </div>
  );
}
